import { useMemo } from 'react';
import { Check, List, Mail, Settings, Users } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { buildUnifiedFolders } from '@/lib/mailboxAggregations';
import {
  STANDARD_UNIFIED_ROLES,
  UNIFIED_ACCOUNT_ID,
  isUnifiedFolderId,
} from '@/constants/mailbox';
import { useUIStore } from '@/stores/uiStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { cn } from '@/lib/utils';
import type { Account, Folder } from '@/types/email';

interface MobileScopeSheetProps {
  accounts: Account[];
  folders: Folder[];
  accountFoldersById: Record<string, Folder[]>;
  selectedAccountId: string | null;
  selectedFolderId: string;
  onNavigate: (accountId: string, folderId: string) => void;
  onOpenSettings: () => void;
}

/**
 * The mobile scope sheet — account / folder / layout picker opened from the
 * list header's scope line. Replaces the sidebar drawer on mail surfaces
 * (round 4). Unified-aware: the "All accounts" scope lists the unified
 * folders (all:inbox, all:custom:*) and every selection routes through the
 * same onFolderSelect used by the desktop sidebar, so unified ids resolve to
 * real (account, mailbox) pairs downstream exactly as before.
 */
export function MobileScopeSheet({
  accounts,
  folders,
  accountFoldersById,
  selectedAccountId,
  selectedFolderId,
  onNavigate,
  onOpenSettings,
}: MobileScopeSheetProps) {
  const open = useUIStore((state) => state.scopeSheetOpen);
  const setOpen = useUIStore((state) => state.setScopeSheetOpen);
  const inboxMode = useSenderGroupingStore((state) => state.inboxMode);
  const setInboxMode = useSenderGroupingStore((state) => state.setInboxMode);

  const actualAccounts = useMemo(
    () => accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID),
    [accounts]
  );
  const hasUnified = actualAccounts.length > 1;

  const unifiedFolders = useMemo(
    () => buildUnifiedFolders(accountFoldersById, actualAccounts),
    [accountFoldersById, actualAccounts]
  );

  const selectedFolder = useMemo(
    () => folders.find((folder) => folder.id === selectedFolderId) ?? null,
    [folders, selectedFolderId]
  );

  // Folder chips for the current scope: standard roles first, then customs the
  // user keeps visible ('always'); 'more'/'never' folders stay desktop-only.
  const folderChips = useMemo(() => {
    const source =
      selectedAccountId === UNIFIED_ACCOUNT_ID
        ? unifiedFolders
        : accountFoldersById[selectedAccountId ?? ''] ?? folders;
    const visible = source.filter((folder) => {
      const visibility = folder.visibility ?? (folder.hidden ? 'more' : 'always');
      return folder.systemFolder || visibility === 'always';
    });
    const roleRank = (folder: Folder) => {
      const role = folder.role ?? folder.unifiedRole ?? undefined;
      const index = role ? STANDARD_UNIFIED_ROLES.indexOf(role as (typeof STANDARD_UNIFIED_ROLES)[number]) : -1;
      return index === -1 ? STANDARD_UNIFIED_ROLES.length : index;
    };
    return [...visible].sort((a, b) => roleRank(a) - roleRank(b) || a.name.localeCompare(b.name));
  }, [selectedAccountId, unifiedFolders, accountFoldersById, folders]);

  // Switching accounts keeps the current folder role when the target account
  // has it (Inbox → Inbox, Sent → Sent); otherwise falls back to inbox.
  const resolveFolderForAccount = (accountId: string): string | null => {
    const currentRole = selectedFolder?.role ?? selectedFolder?.unifiedRole ?? 'inbox';
    if (accountId === UNIFIED_ACCOUNT_ID) {
      const target =
        unifiedFolders.find((folder) => folder.unifiedRole === currentRole) ??
        unifiedFolders.find((folder) => folder.unifiedRole === 'inbox');
      return target?.id ?? null;
    }
    const accountFolders = accountFoldersById[accountId] ?? [];
    const target =
      accountFolders.find((folder) => folder.role === currentRole) ??
      accountFolders.find((folder) => folder.role === 'inbox');
    return target?.id ?? accountFolders[0]?.id ?? null;
  };

  const handleAccountSelect = (accountId: string) => {
    if (accountId === selectedAccountId) {
      return;
    }
    const folderId = resolveFolderForAccount(accountId);
    if (folderId) {
      onNavigate(accountId, folderId);
    }
  };

  const handleFolderSelect = (folder: Folder) => {
    // Unified rows carry the unified account; single-account custom folders in
    // the unified list route to their real (account, folder) pair directly.
    if (selectedAccountId === UNIFIED_ACCOUNT_ID && !isUnifiedFolderId(folder.id)) {
      const sources = folder.sourceFolders ?? [];
      if (sources.length === 1) {
        onNavigate(sources[0].accountId, sources[0].folderId);
        setOpen(false);
        return;
      }
    }
    onNavigate(folder.accountId ?? selectedAccountId ?? UNIFIED_ACCOUNT_ID, folder.id);
    setOpen(false);
  };

  // Layout (individual/groups) only applies to inbox folders — the same gate
  // the desktop ⋯ menu uses.
  const showLayout =
    (selectedFolder?.role ?? selectedFolder?.unifiedRole) === 'inbox';

  const isFolderActive = (folder: Folder) => {
    if (folder.id === selectedFolderId) {
      return true;
    }
    // A single-source custom folder in the unified list is "active" when its
    // real pair is selected.
    const sources = folder.sourceFolders ?? [];
    return sources.length === 1 && sources[0].folderId === selectedFolderId;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        className="max-h-[85dvh] gap-0 overflow-y-auto rounded-t-3xl border-0 pb-[max(env(safe-area-inset-bottom),1rem)] md:hidden"
      >
        <SheetHeader className="pb-1">
          <SheetTitle className="sr-only">Choose account and folder</SheetTitle>
        </SheetHeader>

        {hasUnified && (
          <section className="px-4 pb-2">
            <p className="pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Account
            </p>
            <div className="flex flex-col gap-0.5">
              {[
                { id: UNIFIED_ACCOUNT_ID, label: 'All accounts', icon: Users },
                ...actualAccounts.map((account) => ({
                  id: account.id,
                  label: account.displayName || account.emailAddress,
                  icon: Mail,
                })),
              ].map(({ id, label, icon: Icon }) => {
                const isActive = selectedAccountId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleAccountSelect(id)}
                    className={cn(
                      'flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-medium',
                      isActive ? 'bg-primary/10 text-primary' : 'text-foreground'
                    )}
                  >
                    <Icon className="size-4 shrink-0 opacity-70" />
                    <span className="min-w-0 truncate">{label}</span>
                    {isActive && <Check className="ml-auto size-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="px-4 pb-2 pt-1">
          <p className="pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Folder
          </p>
          <div className="flex flex-wrap gap-1.5">
            {folderChips.map((folder) => {
              const isActive = isFolderActive(folder);
              const unread = folder.count ?? folder.unreadCount ?? 0;
              return (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => handleFolderSelect(folder)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {folder.name}
                  {unread > 0 && (
                    <span
                      className={cn(
                        'text-[11px] font-semibold',
                        isActive ? 'opacity-80' : 'text-primary'
                      )}
                    >
                      {unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {showLayout && (
          <section className="px-4 pb-2 pt-1">
            <p className="pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Layout
            </p>
            <div className="inline-flex rounded-full bg-muted p-1">
              {(
                [
                  { mode: 'flat', label: 'Individual', icon: List },
                  { mode: 'senders', label: 'Groups', icon: Users },
                ] as const
              ).map(({ mode, label, icon: Icon }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => void setInboxMode(mode)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold',
                    inboxMode === mode
                      ? 'bg-card text-primary shadow-sm'
                      : 'text-muted-foreground'
                  )}
                >
                  <Icon className="size-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="border-t border-border/60 px-4 pt-2">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-medium text-muted-foreground"
          >
            <Settings className="size-4 opacity-70" />
            Settings
          </button>
        </section>
      </SheetContent>
    </Sheet>
  );
}
